'use client';
import { useOrderPreview } from './useOrderPreview';
import { cart } from '@/lib/cart';
import { TypeSubFlowV2Translations } from '@/utils/translations/TypeSubFlowV2Translations';
import {
  SELECTED_OPTIONS_KEY,
  SUBFLOW_PRODUCT_ANCHOR,
  addSubscriptionAddOns,
  getBirthDate,
  getBundleAsset,
  getPreviewItems,
  getSelectedOptions,
  extractItems,
  SCHEDULED_ITEM_SCHEDULED_TYPES,
  hasCoursePackItems,
  addCoursePackItems,
  finalizeCart,
  startCheckout,
  redirectToPlayGeniusCheckout,
  redirectToCart,
  QR_DATA_KEY,
} from '@/components/subflow-v3/components/utils';
import { useStepTracking } from './subFlowAnalytics';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { trackAddToCart } from '@/utils/subflowAnalyticsHelpers';
import { noticeError } from '@/utils/newrelicHelpers';
import { ERROR_TYPES, ITEM_ORIGINS } from '@/core/constants';
import { isDateWithinMonthsFromNow } from '@/utils/dates';
import useLocale from './useLocale';
import { getAuthTokenClient, getLocaleInfo } from '@/utils/helpers';
import { PaymentOption } from '@/hooks/usePaymentOptions';
import { SubscriptionAddOn } from './useAddOns';
import useAuth from '@/context/AuthContext';
import { useSubFlowV3Data } from '@/components/subflow-v3/context/SubFlowV3Context';

export type ContinueToCheckoutSettings = {
  selectedPaymentOptionOverride?: PaymentOption;
  subscriptionAddOnsOverride?: SubscriptionAddOn[];
  callback?: () => void;
};

export const useStartCheckout = (
  t?: TypeSubFlowV2Translations,
  version = 'v2',
) => {
  // TODO(subflow-v2): move to LD feature flags
  const locale = useLocale();
  const localeInfo = getLocaleInfo(locale);
  const features = useFlags();
  const { subflow_v2_enable_previous_play_add_ons } = features;
  const isRewriteCheckoutEnable = features['growth__enable-checkout-rewrite'];

  const { data } = useSubFlowV3Data();
  const { data: previewData } = useOrderPreview(
    data?.childInfo || { day: '', month: '', year: '' },
  );
  const { onFlowComplete } = useStepTracking(version);

  const { user } = useAuth();
  const userUuid = user?.attributes['custom:account_manager_id'];

  const continueToCheckout = async (
    settings?: ContinueToCheckoutSettings,
    isSimplifiedSubflow?: boolean,
  ) => {
    if (typeof window === 'undefined' || !data) return;

    let cartData = await cart.get();
    const {
      selectedPaymentOptionOverride,
      subscriptionAddOnsOverride,
      callback,
    } = settings || {};
    const selectedPaymentOption =
      selectedPaymentOptionOverride || data.paymentOption;

    const subsAddOns = subscriptionAddOnsOverride || data.subscriptionAddOns;
    // set language
    cartData.attributes.language = data.language;

    (window as any).cartCurrencyCode = cartData?.currency;

    const { items, previousPlays } = extractItems(cartData.items, data);
    const originalItems = [...cartData.items];
    cartData.items = items;

    const previewItems = await getPreviewItems(previewData, locale);
    const bundleAsset = getBundleAsset(previewItems);
    const selectedOptions = getSelectedOptions(data);

    const subscriptionsPayload = {
      child_name: data?.childInfo.name || t?.noName,
      date_of_birth: getBirthDate(data?.childInfo),
      subscription_anchor: SUBFLOW_PRODUCT_ANCHOR,
      starting_kit_anchor: data?.startingKit?.anchor,
      tagline: selectedPaymentOption?.tagline,
      bundle: {
        badge_title: selectedPaymentOption?.badge_title,
        bundle_title: null,
        is_default: selectedPaymentOption?.is_default,
        kits: null,
        name: selectedPaymentOption?.bundle_title,
        price: selectedPaymentOption?.price,
        savings: selectedPaymentOption?.savings,
        shopify_variant_id: selectedPaymentOption?.shopify_variant_id,
        size: selectedPaymentOption?.size,
        uuid: selectedPaymentOption?.uuid,
      },
      bundleDiscountedPrice: selectedPaymentOption?.price,
      bundleAsset,
      bundleVariantId: selectedPaymentOption?.shopify_variant_id,
      properties: {
        item_origin: ITEM_ORIGINS.SUB_FLOW,
      },
    };

    cartData.subscriptions = [subscriptionsPayload];

    // we are missing necessary bundle information
    if (subscriptionsPayload.bundle.uuid == undefined) {
      const errorPayload = JSON.stringify({ data, cartData });
      noticeError(ERROR_TYPES.SUBFLOW_V3_CHECKOUT_ERROR, errorPayload);
    }

    if (subsAddOns.length) {
      cartData = addSubscriptionAddOns(
        cartData,
        subsAddOns,
        data.paymentOption,
      );
    }

    // TODO - enable gifts and sub in checkout at same time SUBG-821
    // if (giftItem) {
    //   cartData = addGiftItems(giftItem, cartData);
    // }

    if (selectedOptions) {
      cartData.subscriptions[0][SELECTED_OPTIONS_KEY] = selectedOptions;
    }

    if (hasCoursePackItems(cartData?.items)) {
      addCoursePackItems(cartData);
    }

    if (previousPlays?.length > 0 && subflow_v2_enable_previous_play_add_ons) {
      cartData[SCHEDULED_ITEM_SCHEDULED_TYPES.PREVIOUS_PLAY] = previousPlays;
    }

    let lookerInTargetQR = false;
    if (typeof window !== 'undefined') {
      const storage = JSON.parse(
        window.localStorage.getItem(QR_DATA_KEY) || '{}',
      );

      if (storage?.lookerInTargetQR) {
        window.localStorage.removeItem(QR_DATA_KEY);

        const { childInfo } = data;
        if (
          isDateWithinMonthsFromNow({
            year: Number(childInfo.year),
            month: Number(childInfo.month),
            day: Number(childInfo.day),
            months: 3,
          })
        ) {
          lookerInTargetQR = true;
          cartData.subscriptions = [
            {
              ...subscriptionsPayload,
              starting_product_anchor: 'kit-the-charmer',
            },
          ];
        }
      }
    }

    // Logged in user uuid sent to start checkout
    if (userUuid) {
      cartData.user_uuid = userUuid;
    }

    await finalizeCart(cartData, data, subscriptionsPayload, originalItems);

    // we need to refetch the cart for the updated items
    const finalCartData = await cart.get();

    await trackAddToCart(finalCartData);

    const userJwt = await getAuthTokenClient();

    const invoice: any = await startCheckout(cartData, userJwt);

    if (callback) {
      callback();
    }

    if (invoice) {
      onFlowComplete();
      const isRecurringPuf = selectedPaymentOption?.size === 3;

      if (!isSimplifiedSubflow) {
        await redirectToPlayGeniusCheckout(
          invoice.uuid,
          isRecurringPuf,
          localeInfo,
          isRewriteCheckoutEnable,
          data.referralCode,
        );
      } else {
        await redirectToCart();
      }
    }
  };

  return {
    continueToCheckout,
  };
};
